import { Alert, Box, Button, CircularProgress, Typography, alpha, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { httpClient } from '../common/httpclient';
import { MobileActivation } from '../component/auth/MobileAuthStates';
import { useIsMobileShell } from '../hooks/useIsMobileShell';

/**
 * Ativação de assinatura — DESIGN-SYSTEM.md item 1 (Autenticação & telas
 * públicas). Tela de status (loading/sucesso/erro) sem formulário, seguindo
 * o padrão de empty/feedback state genérico (§5.11): ícone centralizado em
 * círculo tracejado/sólido + título em negrito + linha instrutiva menor.
 */
export default function SubscriptionActivate() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';
  const theme = useTheme();
  const isMobileShell = useIsMobileShell();

  const activateAccount = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await httpClient.get('/subscription/activate?code=' + code);

      if (response.status === 200 || response.status === 204) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(response.data?.message || 'Erro ao ativar a conta. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao ativar conta:', error);
      setError(error.response?.data?.message || 'Erro de conexão. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => {
    if (!code) {
      setError('Código de ativação não fornecido');
      setIsLoading(false);
      return;
    }
    activateAccount();
  }, [code, activateAccount]);

  if (isMobileShell) {
    return <MobileActivation isLoading={isLoading} success={success} error={error} onLogin={() => navigate('/')} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        backgroundColor: 'background.default',
      }}
    >
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', filter: 'blur(90px)', opacity: theme.palette.mode === 'dark' ? 0.25 : 0.45, backgroundColor: 'primary.main', top: -140, left: -100 }} />
      </Box>

      <Box
        sx={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 420,
          backgroundColor: alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(24px) saturate(160%)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '26px', boxShadow: theme.customShadows.lg,
          p: '40px 32px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        }}
      >
        {isLoading && (
          <>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', border: `2px dashed ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
              <CircularProgress size={30} sx={{ color: 'primary.main' }} />
            </Box>
            <Typography component="h1" sx={{ fontSize: 18, fontWeight: 800, mb: 0.75 }}>
              Ativando sua conta
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Aguarde enquanto processamos a ativação…
            </Typography>
          </>
        )}

        {!isLoading && success && (
          <>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'success.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
              <CheckCircle2 size={32} />
            </Box>
            <Typography component="h1" sx={{ fontSize: 18, fontWeight: 800, mb: 0.75 }}>
              Conta ativada com sucesso!
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Você será redirecionado para a página de login em instantes…
            </Typography>
          </>
        )}

        {!isLoading && !success && error && (
          <>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'error.bg', color: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
              <XCircle size={32} />
            </Box>
            <Typography component="h1" sx={{ fontSize: 18, fontWeight: 800, mb: 0.75 }}>
              Erro na ativação
            </Typography>
            <Alert severity="error" sx={{ width: '100%', mt: 1, mb: 2.5, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button variant="contained" color="primary" fullWidth sx={{ height: 46 }} onClick={() => navigate('/')}>
              Voltar para o login
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
